import React, { useEffect, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { AxiosResponse } from "axios";

import { AttachmentDTO } from "../../models/EnrollmentHistoryDTO";
import { abrirArchivo } from "./visorArchivo";

/*
	Imágenes y capturas de pantalla en comentarios, rechazos y reenvíos (Esteban, 2026-09-23).
	Se adjuntan con el botón o pegando la captura (Ctrl/⌘+V) en el cuadro de texto.
*/
export const MAX_ADJUNTOS = 5;
const MAX_BYTES = 10 * 1024 * 1024;
export const ACEPTA_ADJUNTOS = "image/*,.pdf,.heic,.heif";

/** Las imágenes que trae un pegado (captura de pantalla). Sin nombre, se les pone uno. */
export const capturasDelPortapapeles = (event: React.ClipboardEvent): File[] => {
	const archivos: File[] = [];
	const items = event.clipboardData ? Array.from(event.clipboardData.items || []) : [];
	items.forEach((item, i) => {
		if (item.kind === "file" && item.type.startsWith("image/")) {
			const f = item.getAsFile();
			if (f) {
				const ext = (f.type.split("/")[1] || "png").replace("jpeg", "jpg");
				archivos.push(new File([f], f.name && f.name !== "image.png" ? f.name : `captura_${Date.now()}_${i + 1}.${ext}`, { type: f.type }));
			}
		}
	});
	return archivos;
};

export interface AdjuntosInputProps {
	files: File[];
	onChange: (files: File[]) => void;
	onError?: (message: string) => void;
	disabled?: boolean;
	etiqueta?: string;
}

/** Botón "Adjuntar imagen" + las elegidas, con miniatura y "Quitar". */
export const AdjuntosInput: React.FC<AdjuntosInputProps> = ({ files, onChange, onError, disabled, etiqueta }) => {
	const agregar = (nuevos: File[]) => {
		const validos = nuevos.filter((f) => f.size <= MAX_BYTES);
		if (validos.length < nuevos.length && onError) {
			onError("Un archivo pasa de 10 MB y no se adjuntó.");
		}
		const todos = [...files, ...validos].slice(0, MAX_ADJUNTOS);
		if (files.length + validos.length > MAX_ADJUNTOS && onError) {
			onError(`Se pueden adjuntar hasta ${MAX_ADJUNTOS} archivos.`);
		}
		onChange(todos);
	};
	const previas = useMemo(() => files.map((f) => (f.type.startsWith("image/") ? URL.createObjectURL(f) : null)), [files]);
	useEffect(() => () => previas.forEach((u) => u && URL.revokeObjectURL(u)), [previas]);

	return (
		<div className="waAdjuntos">
			<label className="btn btn-outline-secondary btn-sm" style={{ position: "static", margin: 0, opacity: disabled ? 0.6 : 1 }}>
				<FontAwesomeIcon icon={solid("image")} />&nbsp;{etiqueta || "Adjuntar imagen"}
				<input type="file" accept={ACEPTA_ADJUNTOS} multiple disabled={disabled} style={{ display: "none" }}
					onChange={(e) => { agregar(Array.from(e.target.files || [])); e.target.value = ""; }} />
			</label>
			{files.map((f, i) => (
				<span key={f.name + "_" + i} className="waAdjunto">
					{previas[i] ? <img src={previas[i] as string} alt="" /> : <FontAwesomeIcon icon={solid("file-pdf")} />}
					<span className="waAdjuntoNombre">{f.name}</span>
					<button type="button" className="btn btn-link btn-sm waOroTexto" style={{ padding: 0 }} disabled={disabled}
						onClick={() => onChange(files.filter((_, j) => j !== i))}>Quitar</button>
				</span>
			))}
		</div>
	);
};

export interface AdjuntosVistaProps {
	adjuntos?: AttachmentDTO[];
	/** Trae el archivo del servidor (con la sesión); la vista no sabe de rutas. */
	cargar: (index: number) => Promise<AxiosResponse<Blob>>;
}

/** Las imágenes de un comentario o de un evento del ticket: miniatura que abre la imagen completa. */
export const AdjuntosVista: React.FC<AdjuntosVistaProps> = ({ adjuntos, cargar }) => {
	const [urls, setUrls] = useState<(string | null)[]>([]);
	useEffect(() => {
		let vivo = true;
		const creadas: string[] = [];
		Promise.all((adjuntos || []).map((a) => (a.mime || "").startsWith("image/")
			? cargar(a.index).then((r) => { const u = URL.createObjectURL(r.data); creadas.push(u); return u; }).catch(() => null)
			: Promise.resolve(null)))
			.then((lista) => { if (vivo) setUrls(lista); });
		return () => { vivo = false; creadas.forEach((u) => URL.revokeObjectURL(u)); };
	}, [JSON.stringify(adjuntos)]);

	if (!adjuntos || adjuntos.length < 1) {
		return null;
	}
	// Sin await antes de abrirArchivo: en celular el window.open de después de la descarga se bloqueaba.
	const abrir = (a: AttachmentDTO, i: number) =>
		abrirArchivo({ origen: "Matrículas", nombre: a.name, urlLista: urls[i], cargar: () => cargar(a.index).then((r) => r.data) }).catch(() => {});
	return (
		<div className="waAdjuntos" style={{ marginTop: 6 }}>
			{adjuntos.map((a, i) => (
				<button type="button" key={"adj_" + a.index} className="waAdjunto boton" title={a.name} onClick={() => abrir(a, i)}>
					{urls[i] ? <img src={urls[i] as string} alt={a.name} /> : <FontAwesomeIcon icon={(a.mime || "").includes("pdf") ? solid("file-pdf") : solid("image")} />}
					<span className="waAdjuntoNombre">{a.name}</span>
				</button>
			))}
		</div>
	);
};
